import { Color, Spacing, Table, TableCol, TablePagination, TableSort, Text } from 'zens-storybook'
import React from 'react'
import { StyleSheet, View, ViewProps, ViewStyle } from 'react-native'
import { useTranslation } from 'react-i18next'
import { LoggingMessage, useProductLogging } from 'zens-store'

import { TablePaginated } from '../table'

import Date from './date'
import LoggingEmpty from './empty'

const styles = StyleSheet.create({
  text: {
    marginHorizontal: Spacing.S,
  },
  message: {
    height: '100%',
  },
  highlight: {
    height: '100%',
    justifyContent: 'center',
    paddingHorizontal: Spacing.S,
    marginHorizontal: -Spacing.XXS,
  },
  error: {
    backgroundColor: Color.lighterPink,
  },
  warning: {
    backgroundColor: Color.lighterCopper,
  },
})

type HighlightStyle = (value: boolean | number) => ViewStyle

interface Props {
  companyId: number
  productId: number
  pagination?: TablePagination
  onPaginationChange?: (pagination: TablePagination) => void
  sort?: TableSort
  onSortChange?: (sort: TableSort) => void
}

const LoggingMessages = (props: Props) => {
  const {
    companyId,
    productId,
    pagination = { page: 0, size: 10 },
    sort = { field: 'updated_at', direction: 'desc' },
    onSortChange,
    onPaginationChange,
  } = props
  const logging = useProductLogging({ companyId, productId, pagination, sort })
  const messages = logging.data?.data || []
  const { t } = useTranslation()

  const rows = messages?.map((message) => ({ data: message }))
  const renderDate = (message: LoggingMessage, key: keyof LoggingMessage) => {
    const date = message[key]
    if (!date) return undefined
    return <Date date={date} />
  }

  const renderTextHighlight = (message: LoggingMessage, key: keyof LoggingMessage) => {
    const value = message[key]
    if (value === undefined || value === null) return undefined
    return (
      <Text level={value ? 5 : 6} style={styles.text}>
        {value ? 1 : 0}
      </Text>
    )
  }

  const renderBackgroundHighlight = (
    message: LoggingMessage,
    key: keyof LoggingMessage,
    highlightStyle?: HighlightStyle,
    binary?: false,
  ) => {
    const value = message[key]
    if (value === undefined || value === null) return undefined
    const style = highlightStyle?.(value)
    return (
      <View style={[styles.highlight, value, style]}>
        <Text>{binary === false ? value : value ? 1 : 0}</Text>
      </View>
    )
  }
  const renderFirmware = (message: LoggingMessage, key: string) => {
    const version = message[key as keyof LoggingMessage]
    if (!version) return undefined
    return <Text style={styles.text}>{t('V{{version}}', { version })}</Text>
  }

  const falseError = (value: boolean | number) => ((!value && styles.error) as ViewStyle) || undefined
  const trueError = (value: boolean | number) => ((value && styles.error) as ViewStyle) || undefined
  const csqStyle = ((value: number) => {
    if (value < 6) return styles.error
    else if (value < 8) return styles.warning
  }) as HighlightStyle

  const cols: TableCol<LoggingMessage>[] = [
    {
      key: 'updated_at',
      title: t('Received at'),
      render: (message) => renderDate(message, 'updatedAt'),
    },
    {
      key: 'time',
      title: t('Sent at'),
      render: (message) => renderDate(message, 'sentAt'),
    },
    {
      key: 'occupancy',
      title: t('Occupied'),
      render: (message) => renderTextHighlight(message, 'isOccupied'),
      size: {
        width: 100,
      },
    },
    {
      key: 'charging',
      title: t('Charging'),
      render: (message) => renderTextHighlight(message, 'isCharging'),
      size: {
        width: 100,
      },
    },
    {
      key: 'led_connected',
      title: t('Led cnnct'),
      render: (message) => renderBackgroundHighlight(message, 'ledConnected', falseError),
      size: {
        width: 100,
      },
    },
    {
      key: 'sensor_connected',
      title: t('Prscn cnnct'),
      render: (message) => renderBackgroundHighlight(message, 'sensorConnected', falseError),
      size: {
        width: 100,
      },
    },
    {
      key: 'pdp_cycle',
      title: t('PDP'),
      render: (message) => renderBackgroundHighlight(message, 'pdpCycle', trueError),
      size: {
        width: 100,
      },
    },
    {
      key: 'power_cycle',
      title: t('Power cycle'),
      render: (message) => renderBackgroundHighlight(message, 'powerCycle', trueError),
      size: {
        width: 100,
      },
    },
    {
      key: 'csq',
      title: t('CSQ'),
      render: (message) => renderBackgroundHighlight(message, 'csq', csqStyle, false),
      size: {
        width: 100,
      },
    },
    {
      key: 'firmware',
      title: t('Firmware'),
      render: renderFirmware,
      size: {
        width: 100,
      },
    },
  ]

  return (
    <TablePaginated
      rows={rows}
      cols={cols}
      pagination={pagination}
      count={logging.data?.meta?.total}
      renderCount={() =>
        t('{{page}} of {{numberOfPages}}', {
          page: pagination.page + 1,
          numberOfPages: Math.ceil((logging.data?.meta?.total || 0) / pagination.size),
        })
      }
      sizeLabel={t('Messages per page')}
      onPaginationChange={onPaginationChange}
      cellStyle={styles.message}
      sort={sort}
      onSortChange={onSortChange}
      dataIsSorted
      ListEmptyComponent={<LoggingEmpty />}
    />
  )
}

interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()

  const cols: TableCol<LoggingMessage>[] = [
    {
      key: 'updated_at',
      title: t('Received at'),
    },
    {
      key: 'time',
      title: t('Sent at'),
    },
    {
      key: 'occupancy',
      title: t('Occupied'),
      size: {
        width: 100,
      },
    },
    {
      key: 'charging',
      title: t('Charging'),
      size: {
        width: 100,
      },
    },
    {
      key: 'led_connected',
      title: t('Led cnnct'),
      size: {
        width: 100,
      },
    },
    {
      key: 'sensor_connected',
      title: t('Prscn cnnct'),
      size: {
        width: 100,
      },
    },
    {
      key: 'pdp_cycle',
      title: t('PDP'),
      size: {
        width: 100,
      },
    },
    {
      key: 'power_cycle',
      title: t('Power cycle'),
      size: {
        width: 100,
      },
    },
    {
      key: 'csq',
      title: t('CSQ'),
      size: {
        width: 100,
      },
    },
    {
      key: 'firmware',
      title: t('Firmware'),
      size: {
        width: 100,
      },
    },
  ]

  return <Table.Skeleton cols={cols} {...props} />
}
LoggingMessages.Skeleton = Skeleton

export default LoggingMessages
